import '../stylesheets/application.scss';
import { DefaultApolloClient } from '@vue/apollo-composable';
import '../images/favicon.ico';
import '../images/social/sc_opengraph-image.png';

import { isNavigationFailure } from 'vue-router';
import { createSittercityApp, initializeClientIntegrations } from '../core/sittercity';
import App from '../apps/App.vue';
import { setupApollo } from '../lib/apollo_setup';

import router from '../router';
import store from '../store';
import { startSiftTracking } from '../lib/sift';

// Mirrors components/spinner/Spinner.vue. Inlined as raw markup because this placeholder is
// rendered into #app before Vue mounts, so the SFC cannot be used here. The spin animation
// comes from the global `animate-spin` utility loaded via application.scss.
const LOADING_HTML = `
  <div style="display: flex; justify-content: center; align-items: center; padding: 50px;">
    <svg class="animate-spin" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Loading">
      <path
        d="M17.7502 2.04065L16.2502 4.63872C12.1847 2.29151 6.98617 3.68445 4.63896 7.74994C2.29175 11.8154 3.68469 17.0139 7.75018 19.3612C11.8157 21.7084 17.0142 20.3154 19.3614 16.2499L21.9595 17.7499C18.7838 23.2503 11.7505 25.1349 6.25018 21.9592C0.749814 18.7836 -1.13475 11.7503 2.04089 6.24994C5.21653 0.749575 12.2498 -1.13499 17.7502 2.04065Z"
        fill="#FF0064"
      />
    </svg>
  </div>
`;

interface AppInitializationError extends Error {
  code?: string;
  context?: string;
}

let app: ReturnType<typeof createSittercityApp> | null = null;

function handleAppError(error: unknown, context: string): AppInitializationError {
  const appError = error instanceof Error ? error : new Error(`Unknown error in ${context}: ${String(error)}`);

  (appError as AppInitializationError).context = context;
  return appError as AppInitializationError;
}

const FATAL_ERROR_HTML = `
  <div style="padding: 20px; text-align: center; color: #e74c3c;">
    <h2>Application Error</h2>
    <p>We're sorry, but the application failed to load. Please refresh the page or contact support if the problem persists.</p>
  </div>
`;

function showFatalError(): void {
  const errorContainer = document.getElementById('app') ?? document.body;
  if (errorContainer) {
    errorContainer.innerHTML = FATAL_ERROR_HTML;
  }
}

async function initializeApp(): Promise<void> {
  try {
    const appNode = document.getElementById('app');

    if (appNode === null) {
      throw new Error('#app mount node is missing');
    }

    appNode.innerHTML = LOADING_HTML;

    const apolloProvider = setupApollo();

    app = createSittercityApp(App);
    app.provide(DefaultApolloClient, apolloProvider.defaultClient);

    await initializeClientIntegrations();

    app.use(router);
    app.use(store);
    app.use(apolloProvider);

    try {
      await router.isReady();
    } catch (navigationError) {
      // A guard aborted the initial navigation to perform a full-page redirect (a legacy/external
      // onboarding step, or /login). The browser is already navigating away, so keep the loading
      // spinner up rather than flashing the fatal error panel. Genuine errors are not
      // NavigationFailures and re-throw to the catch below.
      if (isNavigationFailure(navigationError)) {
        return;
      }
      throw navigationError;
    }

    try {
      startSiftTracking();
    } catch (error) {
      console.warn('Sift setup failed:', error);
    }

    app.mount('#app');
  } catch (error) {
    const appError = handleAppError(error, 'app initialization');
    console.error('Failed to initialize Sittercity app:', appError);

    if (app) {
      try {
        app.unmount();
      } catch (unmountError) {
        console.error('Failed to unmount app during cleanup:', unmountError);
      }
    }

    showFatalError();

    throw appError;
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  try {
    await initializeApp();
  } catch {
    // Error already logged in initializeApp
  }
});
